import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { API_BASE_URL } from '@/config';
import { SupportTicket, UserStatus } from '@/types';
import { useToast } from '@/contexts/ToastContext';
import { Loader2, ShieldCheck, ShieldOff, RefreshCw } from 'lucide-react';

interface AppealTicket extends SupportTicket {
    submittedBy: {
        id: string;
        name: string;
        email: string;
        status: UserStatus;
    };
}

const AdminModeration: React.FC = () => {
    const [appeals, setAppeals] = useState<AppealTicket[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const { token } = useAuth();
    const { notify } = useToast();

    const fetchAppeals = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await fetch(`${API_BASE_URL}/api/support/moderation-appeals`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to fetch appeals.');
            }
            const data = await response.json();
            setAppeals(data);
        } catch (err: any) {
            setError(err.message);
            notify(err.message, 'error');
        } finally {
            setIsLoading(false);
        }
    }, [token, notify]);

    useEffect(() => {
        fetchAppeals();
    }, [fetchAppeals]);

    const handleApprove = async (ticketId: string, userId: string) => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/user/${userId}/unsuspend`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({ ticketId }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to approve appeal.');
            }

            notify('Appeal Approved! User account has been reactivated.', 'success');
            fetchAppeals(); // Refresh the list
        } catch (err: any) {
            notify(err.message, 'error');
        }
    };

    const handleReject = async (ticketId: string) => {
        if (!window.confirm('Are you sure you want to reject this appeal? This action cannot be undone.')) return;

        try {
            const response = await fetch(`${API_BASE_URL}/api/support/appeals/${ticketId}/reject`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to reject appeal.');
            }

            notify('Appeal Rejected. The ticket has been closed.', 'success');
            fetchAppeals(); // Refresh the list
        } catch (err: any) {
            notify(err.message, 'error');
        }
    };

    if (isLoading) {
        return (
            <div className="flex justify-center items-center h-64 bg-kala-900 rounded-lg">
                <Loader2 className="w-12 h-12 text-kala-secondary animate-spin" />
                <p className="ml-4 text-2xl text-kala-400">Loading Appeals...</p>
            </div>
        );
    }

    if (error) {
        return <div className="text-center p-8 text-red-500 bg-red-500/10 rounded-lg">Error: {error}</div>;
    }

    return (
        <div className="bg-kala-900 text-white rounded-lg p-6 shadow-xl">
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold">Pending Appeals</h2>
                <button onClick={fetchAppeals} className="p-2 rounded-full hover:bg-kala-800 transition-colors">
                    <RefreshCw className={`w-5 h-5 ${isLoading ? 'animate-spin' : ''}`} />
                </button>
            </div>
            {appeals.length === 0 ? (
                <div className="text-center py-12 border-2 border-dashed border-kala-700 rounded-lg">
                    <p className="text-kala-400">No pending zero-tolerance appeals.</p>
                </div>
            ) : (
                <div className="space-y-4">
                    {appeals.map((ticket) => (
                        <div key={ticket.id} className="bg-kala-800 border border-kala-700 p-5 rounded-xl transition-all hover:border-kala-secondary/50">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-center">
                                <div className="md:col-span-2 space-y-2">
                                    <p className="text-lg font-semibold text-white">{ticket.subject}</p>
                                    <p className="text-sm text-kala-300 font-mono">User: {ticket.submittedBy.name} ({ticket.submittedBy.email})</p>
                                    <p className="text-sm text-kala-400">Status: <span className={ticket.submittedBy.status === 'ACTIVE' ? 'text-green-400' : 'text-red-400 font-bold'}>{ticket.submittedBy.status}</span></p>
                                    <p className="text-xs text-kala-500">Ticket ID: {ticket.id}</p>
                                </div>
                                <div className="flex items-center justify-end space-x-3">
                                     <button onClick={() => handleApprove(ticket.id, ticket.submittedBy.id)} className="flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white font-bold py-2 px-4 rounded-lg transition-colors">
                                        <ShieldCheck className="w-5 h-5"/>
                                        Approve
                                    </button>
                                     <button onClick={() => handleReject(ticket.id)} className="flex items-center justify-center gap-2 bg-red-500 hover:bg-red-600 text-white font-bold py-2 px-4 rounded-lg transition-colors">
                                        <ShieldOff className="w-5 h-5"/>
                                        Reject
                                    </button>
                                </div>
                            </div>
                             <div className="mt-4 pt-4 border-t border-kala-700">
                                <h4 className="font-bold mb-2">Description:</h4>
                                <p className="text-sm text-kala-300 bg-kala-900/50 p-3 rounded">{ticket.description}</p>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default AdminModeration;
